import { Analytics } from '@analytics';
import { Button, MenuItem, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { AddCircle as AddCircleIcon } from '@material-ui/icons';
import { useIsFeatureForCompanyEnabled } from '@utils/hooks';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import ProPlanModal from '../ProPlanModal';
import messages from './messages';

const useStyles = makeStyles({
  button: {
    color: '#fff',
    fontSize: '14px',
    fontWeight: 600,
    boxShadow: 'none',
    backgroundColor: '#4f46e5',
    borderRadius: '12px',
    whiteSpace: 'nowrap',
    height: 'fit-content',
    textTransform: 'none',

    '&:hover': {
      backgroundColor: '#4338ca',
      boxShadow: 'none'
    }
  },
  menuItemText: {
    marginRight: '8px'
  },
  proBadge: {
    fontSize: '10px!important',
    padding: '2px 8px',
    borderRadius: '6px',
    color: '#fff',
    backgroundColor: '#1e293b',
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase'
  }
});

const AccessControlledButton = ({
  feature,
  tooltipTitle = '',
  as = 'Button',
  title,
  onClick,
  withIcon = true,
  icon = null,
  elementStyle = '',
  shouldCheckFeature = true,
  isHiddenWhenDisabled = false
}) => {
  const intl = useIntl();
  const [isOpenModal, setIsOpenModal] = React.useState(false);
  const classes = useStyles();
  const isFeatureForCompanyEnabled = useIsFeatureForCompanyEnabled();
  const isFeatureEnabled = useMemo(() => {
    if (shouldCheckFeature) {
      return isFeatureForCompanyEnabled(feature);
    }

    return true;
  }, [feature, isFeatureForCompanyEnabled, shouldCheckFeature]);
  const buttonIcon = withIcon ? icon ?? <AddCircleIcon /> : null;

  const openModal = () => {
    Analytics.track('Pro Plan Modal Opened', { feature, title });
    setIsOpenModal(true);
  };

  const ProBadge = () => <div className={classes.proBadge}>{intl.formatMessage(messages.pro)}</div>;

  const getContent = () => {
    switch (as) {
      case 'Button':
        return (
          <Button
            className={`${classes.button} ${elementStyle}`}
            variant="contained"
            color="primary"
            endIcon={isFeatureEnabled ? buttonIcon : <ProBadge />}
            onClick={isFeatureEnabled ? onClick : openModal}>
            {title}
          </Button>
        );
      case 'MenuItem':
        return (
          <MenuItem className={elementStyle} onClick={isFeatureEnabled ? onClick : openModal}>
            <span className={classes.menuItemText}>{title}</span>
            {!isFeatureEnabled ? <ProBadge /> : null}
          </MenuItem>
        );
      default:
        return <div />;
    }
  };

  if (isHiddenWhenDisabled && !isFeatureEnabled) {
    return null;
  }

  return (
    <>
      <Tooltip
        title={!isFeatureEnabled ? intl.formatMessage(messages.proPlanTooltip) : tooltipTitle}>
        {getContent()}
      </Tooltip>

      <ProPlanModal isOpen={isOpenModal} onClose={() => setIsOpenModal(false)} />
    </>
  );
};

export default AccessControlledButton;
